<template>
	<div class="categories-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Category Administration</h1>
			<p class="subtitle">Manage and configure voucher categories</p>
		</div>

		<!-- Alerts -->
		<transition name="fade">
			<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
				<span class="alert-message">{{ alertMessage }}</span>
				<button class="alert-close" @click="showAlert = false">✕</button>
			</div>
		</transition>

		<div class="categories-container">
			<!-- Categories List Section -->
			<AdminListSection
				title="Categories"
				:items="categories"
				:selected-id="selectedCategory?.id"
				:loading="loading"
				search-placeholder="Search categories..."
				empty-message="No categories found"
				:search-fields="['buttonTitle', 'pageTitle']"
				@select="selectCategory"
				@refresh="refreshCategories"
			>
				<template #item="{ item }">
					<div class="category-item-content">
						<div class="category-info">
							<div class="category-name">{{ item.buttonTitle }}</div>
							<div class="category-meta">
								<span class="badge" :class="{ enabled: item.active, disabled: !item.active }">
									{{ item.active ? 'Active' : 'Inactive' }}
								</span>
							</div>
						</div>
					</div>
				</template>
				<template #item-actions="{ item }">
					<div class="category-order">Order: {{ item.order }}</div>
				</template>
			</AdminListSection>

			<!-- Category Details Section -->
			<div v-if="selectedCategory" ref="categoryDetailsSection" class="category-details-section" @mounted="scrollToDetails">
				<div class="section-header">
					<h2>{{ selectedCategory.buttonTitle || 'Category Details' }}</h2>
					<AdminHeaderActions :loading="loading" :has-id="!!selectedCategory.id" @save="saveCategory" @delete="showDeleteConfirm" @new="newCategory" />
				</div>

				<div class="details-grid">
					<!-- Basic Information -->
					<div class="form-section">
						<h3>Basic Information</h3>
						<div class="form-group">
							<label>Button Title</label>
							<input type="text" v-model="selectedCategory.buttonTitle" class="form-input" />
						</div>
						<div class="form-group">
							<label>Page Title</label>
							<input type="text" v-model="selectedCategory.pageTitle" class="form-input" />
						</div>
						<div class="form-group">
							<label>Order</label>
							<input type="number" v-model.number="selectedCategory.order" class="form-input" />
						</div>
						<div class="form-group checkbox-group">
							<label class="checkbox-label">
								<input type="checkbox" v-model="selectedCategory.active" />
								Active
							</label>
						</div>
					</div>

					<!-- Filters & Configuration -->
					<div class="form-section">
						<h3>Configuration</h3>
						<div class="form-group">
							<label>Provider Filters</label>
							<textarea v-model="selectedCategory.providerFilters" class="form-textarea" placeholder="Enter provider filters..."></textarea>
						</div>
					</div>

					<!-- Images -->
					<div class="form-section">
						<h3>Images</h3>
						<div class="form-group">
							<label>Button Image URL</label>
							<input type="text" v-model="selectedCategory.buttonImage" class="form-input" />
						</div>
						<div class="form-group">
							<label>Page Image URL</label>
							<input type="text" v-model="selectedCategory.pageImage" class="form-input" />
						</div>
					</div>

					<!-- Image Upload -->
					<div class="form-section">
						<h3>Image Upload</h3>
						<div class="image-upload-grid">
							<div class="upload-item">
								<label>Button Image</label>
								<input type="file" @change="onImageSelected('button')" class="file-input" />
								<button class="action-btn secondary" @click="uploadImage('buttonImage')" :disabled="!uploadFile" v-if="uploadFile">
									📤 Upload
								</button>
								<div v-if="selectedCategory.buttonImage" class="preview-item">
									<img :src="selectedCategory.buttonImage" alt="Button Image" class="preview-image" />
								</div>
							</div>
							<div class="upload-item">
								<label>Page Image</label>
								<input type="file" @change="onImageSelected('page')" class="file-input" />
								<button class="action-btn secondary" @click="uploadImage('pageImage')" :disabled="!uploadFile" v-if="uploadFile">
									📤 Upload
								</button>
								<div v-if="selectedCategory.pageImage" class="preview-item">
									<img :src="selectedCategory.pageImage" alt="Page Image" class="preview-image" />
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Delete Confirmation Modal -->
				<transition name="fade">
					<div v-if="deleteConfirm" class="modal-backdrop">
						<div class="modal-dialog">
							<div class="modal-header">
								<h3>Confirm Delete</h3>
								<button class="modal-close" @click="deleteConfirm = false">✕</button>
							</div>
							<div class="modal-body">
								<p>Are you sure you want to delete <strong>{{ selectedCategory.buttonTitle }}</strong>?</p>
								<p>This action cannot be undone.</p>
							</div>
							<div class="modal-footer">
								<button class="action-btn secondary" @click="deleteConfirm = false">Cancel</button>
								<button class="action-btn danger" @click="deleteCategory">Delete</button>
							</div>
						</div>
					</div>
				</transition>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state-section">
				<p>Select a category to view details</p>
			</div>
		</div>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';
import AdminListSection from '@/admin/components/AdminListSection.vue';

export default {
	name: 'CategoriesPage',
	components: {
		AdminHeaderActions,
		AdminListSection
	},
	mixins: [AdminServices],
	data() {
		return {
			loading: false,
			categories: [],
			selectedCategory: null,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			uploadFile: null,
			uploadField: null,
			deleteConfirm: false
		}
	},
	computed: {},
	methods: {
		loadCategories() {
			this._getCategories((data) => {
				if (data.length > 0 && !this.selectedCategory) {
					this.selectCategory(data[0]);
				}
			});
		},
		selectCategory(category) {
			this.selectedCategory = JSON.parse(JSON.stringify(category));
			this.uploadFile = null;

			// Auto-scroll to details section on mobile
			if (window.innerWidth <= 768) {
				this.$nextTick(() => {
					const detailsSection = this.$refs.categoryDetailsSection;
					if (detailsSection) {
						detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				});
			}
		},
		newCategory() {
			this.selectedCategory = {
				buttonTitle: '',
				pageTitle: '',
				providerFilters: '',
				buttonImage: '',
				pageImage: '',
				order: 0,
				active: true
			};
			this.uploadFile = null;
		},
		saveCategory() {
			if (!this.selectedCategory.buttonTitle) {
				this.showAlertMessage('Button Title is required', 'error');
				return;
			}

			this.loading = true;
			if (this.selectedCategory.id) {
				// Update existing
				this._updateCategory(this.selectedCategory.id, this.selectedCategory,
					() => {
						this.showAlertMessage('Category saved successfully', 'success');
						this.loading = false;
					},
					() => {
						this.showAlertMessage('Failed to save category', 'error');
						this.loading = false;
					}
				);
			} else {
				// Create new
				this._createCategory(this.selectedCategory,
					(response) => {
						this.selectedCategory = response;
						this.showAlertMessage('Category created successfully', 'success');
						this.loading = false;
					},
					() => {
						this.showAlertMessage('Failed to create category', 'error');
						this.loading = false;
					}
				);
			}
		},
		showDeleteConfirm() {
			this.deleteConfirm = true;
		},
		deleteCategory() {
			this.deleteConfirm = false;
			this.loading = true;
			this._deleteCategory(this.selectedCategory.id,
				() => {
					this.showAlertMessage('Category deleted successfully', 'success');
					this.selectedCategory = null;
					this.loading = false;
				},
				() => {
					this.showAlertMessage('Failed to delete category', 'error');
					this.loading = false;
				}
			);
		},
		refreshCategories() {
			this.loadCategories();
		},
		onImageSelected(field) {
			const input = event.target;
			if (input.files && input.files[0]) {
				this.uploadFile = input.files[0];
				this.uploadField = field;
			}
		},
		uploadImage(field) {
			if (!this.uploadFile) return;

			this.loading = true;
			const reader = new FileReader();
			reader.onload = (e) => {
				const base64 = e.target.result.split(',')[1];
				this._uploadCategoryImage({
					content: base64,
					fileName: this.uploadFile.name
				},
					(response) => {
						if (field === 'buttonImage') {
							this.selectedCategory.buttonImage = response.uri;
						} else if (field === 'pageImage') {
							this.selectedCategory.pageImage = response.uri;
						}
						this.showAlertMessage('Image uploaded successfully', 'success');
						this.uploadFile = null;
						this.loading = false;
					},
					() => {
						this.showAlertMessage('Failed to upload image', 'error');
						this.loading = false;
					}
				);
			};
			reader.readAsDataURL(this.uploadFile);
		},
		showAlertMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			setTimeout(() => {
				this.showAlert = false;
			}, 4000);
		}
	},
	mounted() {
		this.loadCategories();
	}
}
</script>

<style scoped>
.categories-page {
	min-height: 100%;
	padding: 20px;
}

/* Header */
.page-header {
	margin-bottom: 30px;
}

.page-header h1 {
	margin: 0 0 5px 0;
	color: var(--admin-text-primary);
	font-size: 28px;
	font-weight: 600;
}

.subtitle {
	margin: 0;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

/* Alerts */
.alert {
	margin-bottom: 20px;
	padding: 12px 15px;
	border-radius: var(--border-radius-s);
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background-color: rgba(76, 175, 80, 0.1);
	color: #2e7d32;
	border: 1px solid rgba(76, 175, 80, 0.3);
}

.alert-error {
	background-color: rgba(211, 47, 47, 0.1);
	color: #c62828;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.alert-close {
	background: none;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 16px;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Container */
.categories-container {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
}

/* Categories List Section */
.categories-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	min-height: 600px;
	max-height: 80vh;
	margin-right: 10px;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 15px;
}

.section-header h2 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.header-actions {
	display: flex;
	gap: 10px;
}

.search-box {
	margin-bottom: 15px;
}

.search-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-primary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.search-input::placeholder {
	color: var(--admin-text-secondary);
}

.search-input:focus {
	outline: none;
	border-color: var(--admin-text-primary);
	background-color: var(--admin-bg-primary);
}

.loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
}

.spinner {
	border: 3px solid var(--admin-border);
	border-top: 3px solid var(--admin-text-primary);
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin-bottom: 10px;
}

@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

.categories-list {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.category-item {
	padding: 12px;
	background-color: var(--admin-bg-primary);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.category-item:hover {
	background-color: var(--admin-hover-bg);
	border-color: var(--admin-text-primary);
}

.category-item.active {
	background-color: var(--admin-active-bg);
	border-color: var(--background-color-alt6);
}

.category-item-content {
	flex: 1;
	min-width: 0;
}

.category-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.category-name {
	color: var(--admin-text-primary);
	font-weight: 600;
	font-size: 14px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.category-meta {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
}

.badge {
	display: inline-block;
	padding: 4px 8px;
	border-radius: var(--border-radius-s);
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
}

.badge.enabled {
	background-color: rgba(76, 175, 80, 0.2);
	color: #2e7d32;
}

.badge.disabled {
	background-color: rgba(158, 158, 158, 0.2);
	color: #616161;
}

.category-order {
	color: var(--admin-text-secondary);
	font-size: 12px;
	white-space: nowrap;
	margin-left: 10px;
}

.empty-state {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: var(--admin-text-secondary);
	text-align: center;
}

/* Details Section */
.category-details-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	overflow-y: auto;
	max-height: 80vh;
}

.empty-state-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 40px 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--admin-text-secondary);
	text-align: center;
	min-height: 400px;
}

.details-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 20px;
}

.form-section {
	background-color: var(--admin-bg-primary);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	padding: 15px;
}

.form-section h3 {
	margin: 0 0 15px 0;
	color: var(--admin-text-primary);
	font-size: 14px;
	font-weight: 600;
	padding-bottom: 10px;
	border-bottom: 1px solid var(--admin-border);
}

.form-group {
	margin-bottom: 15px;
}

.form-group:last-child {
	margin-bottom: 0;
}

.form-group label {
	display: block;
	margin-bottom: 6px;
	color: var(--admin-text-primary);
	font-size: 12px;
	font-weight: 600;
}

.form-input,
.form-textarea {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 14px;
	font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
	outline: none;
	border-color: var(--admin-text-primary);
	background-color: var(--admin-bg-secondary);
}

.form-input:disabled {
	background-color: var(--admin-border);
	color: var(--admin-text-secondary);
	cursor: not-allowed;
}

.form-textarea {
	resize: vertical;
	min-height: 80px;
}

.checkbox-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.checkbox-label {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: var(--admin-text-primary);
	font-size: 13px;
	font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
	cursor: pointer;
	width: 16px;
	height: 16px;
}

.image-upload-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 15px;
}

.upload-item {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.file-input {
	padding: 8px;
	border: 1px dashed var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	cursor: pointer;
	color: var(--admin-text-primary);
	accent-color: var(--background-color-alt6);
}

.file-input::file-selector-button {
	background-color: var(--background-color-alt6);
	color: white;
	border: none;
	padding: 8px 16px;
	border-radius: var(--border-radius-s);
	cursor: pointer;
	font-weight: 600;
	margin-right: 10px;
	transition: all 0.2s ease;
}

.file-input::file-selector-button:hover {
	opacity: 0.9;
	transform: translateY(-1px);
}

.preview-item {
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	padding: 8px;
	background-color: var(--admin-bg-secondary);
}

.preview-image {
	max-width: 100%;
	max-height: 150px;
	border-radius: var(--border-radius-s);
}

/* Action Buttons */
.action-btn {
	padding: 10px 16px;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.action-btn.primary {
	background-color: var(--background-color-alt6);
	color: white;
}

.action-btn.primary:hover:not(:disabled) {
	background-color: #ffb800;
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.action-btn.secondary {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.secondary:hover:not(:disabled) {
	background-color: var(--admin-active-bg);
}

.action-btn.danger {
	background-color: rgba(211, 47, 47, 0.1);
	color: #d32f2f;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.action-btn.danger:hover:not(:disabled) {
	background-color: #d32f2f;
	color: white;
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Modal */
.modal-backdrop {
	display: fixed;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-dialog {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	box-shadow: var(--box-shadow);
	max-width: 500px;
	width: 90%;
	max-height: 90vh;
	overflow-y: auto;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.modal-header h3 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
}

.modal-close {
	background: none;
	border: none;
	color: var(--admin-text-primary);
	font-size: 24px;
	cursor: pointer;
	padding: 0;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
}

.modal-close:hover {
	background-color: var(--admin-hover-bg);
}

.modal-body {
	padding: 20px;
	color: var(--admin-text-primary);
}

.modal-body p {
	margin: 0 0 10px 0;
}

.modal-body p:last-child {
	margin-bottom: 0;
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 20px;
	border-top: 1px solid var(--admin-border);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Responsive */
@media (max-width: 1200px) {
	.categories-container {
		grid-template-columns: 250px 1fr;
	}
}

@media (max-width: 768px) {
	.categories-page {
		padding: 8px;
		overflow-x: hidden;
	}

	.categories-container {
		grid-template-columns: 1fr;
		gap: 12px;
		overflow-x: hidden;
	}

	.categories-list-section {
		max-height: 250px;
		margin-bottom: 0;
		margin-right: 0;
		padding: 10px;
		overflow-x: hidden;
	}

	.category-details-section {
		padding: 10px;
		overflow-x: hidden;
	}

	.section-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		padding-bottom: 10px;
	}

	.section-header h2 {
		font-size: 16px;
		margin: 0;
	}

	.header-actions {
		width: 100%;
		flex-wrap: wrap;
		gap: 6px;
	}

	.header-actions .action-btn {
		flex: 1;
		min-width: 60px;
		padding: 6px 8px;
		font-size: 11px;
	}

	.details-grid {
		gap: 10px;
		display: grid;
		grid-template-columns: 1fr;
		overflow-x: hidden;
	}

	.page-header h1 {
		font-size: 24px;
	}

	.form-section {
		padding: 8px;
		margin: 0;
		box-sizing: border-box;
		overflow-x: hidden;
	}

	.form-section h3 {
		font-size: 11px;
		margin: 0 0 8px 0;
		padding-bottom: 5px;
	}

	.form-group {
		margin-bottom: 6px;
		overflow-x: hidden;
	}

	.form-group label {
		font-size: 10px;
		margin-bottom: 2px;
		display: block;
		word-break: break-word;
	}

	.form-input,
	.form-textarea {
		padding: 5px;
		font-size: 11px;
		width: 100%;
		box-sizing: border-box;
		min-width: 0;
		overflow-x: hidden;
	}

	.form-textarea {
		min-height: 50px;
	}

	.image-upload-grid {
		grid-template-columns: 1fr;
		gap: 6px;
		overflow-x: hidden;
	}

	.upload-item {
		gap: 4px;
		overflow-x: hidden;
	}

	.modal-dialog {
		max-width: calc(100vw - 10px);
		width: calc(100vw - 10px);
		max-height: 85vh;
		margin: 5px;
		box-sizing: border-box;
	}

	.modal-header {
		padding: 15px;
	}

	.modal-header h3 {
		font-size: 16px;
	}

	.modal-body {
		padding: 15px;
	}

	.modal-footer {
		padding: 15px;
	}
}

@media (max-width: 480px) {
	.categories-page {
		padding: 15px;
		padding-left: 10px;
	}

	.categories-container {
		grid-template-columns: 1fr;
	}

	.page-header {
		margin-bottom: 20px;
	}

	.page-header h1 {
		font-size: 20px;
	}

	.subtitle {
		font-size: 12px;
	}

	.categories-list-section {
		max-height: 350px;
		margin-bottom: 15px;
		margin-right: 0;
		padding: 12px;
	}

	.search-input {
		padding: 8px 10px;
		font-size: 12px;
	}

	.category-item {
		padding: 12px;
		gap: 8px;
	}

	.category-name {
		font-size: 12px;
	}

	.header-actions {
		width: 100%;
	}

	.header-actions .action-btn {
		font-size: 12px;
		padding: 8px 12px;
		flex: 1;
	}

	.image-upload-grid {
		grid-template-columns: 1fr;
	}
}

/* Light mode overrides */
:root.light-mode .categories-list-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .category-details-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .form-section {
	background-color: #f9f9f9 !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .category-item {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .category-item.active {
	background-color: #f0f0f0 !important;
	border-color: var(--background-color-alt6) !important;
}

:root.light-mode .modal-dialog {
	background-color: #ffffff !important;
}

:root.light-mode .modal-header {
	border-bottom-color: #e0e0e0 !important;
}

:root.light-mode .modal-body {
	background-color: #ffffff !important;
}

:root.light-mode .modal-footer {
	border-top-color: #e0e0e0 !important;
	background-color: #f9f9f9 !important;
}

:root.light-mode .search-input {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
	color: var(--admin-text-primary) !important;
}

:root.light-mode .search-input::placeholder {
	color: #999999 !important;
}

:root.light-mode .file-input {
	background-color: #f9f9f9 !important;
	border-color: #d0d0d0 !important;
	color: var(--admin-text-primary) !important;
}

:root.light-mode .file-input::file-selector-button {
	background-color: var(--background-color-alt6) !important;
	color: white !important;
}

:root.light-mode .alert {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}
</style>
